import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
    Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getCustomerEdit, saveCustomerEdit } from "../../../services/customerService";
import { CustomerEdit } from "../../../models/customer/customerEdit";
import { showApiError } from "../../framework/formUtils";

type Props = {
    open: boolean;
    onClose: () => void;
    onSaved: (customer: { id: string; name: string; active: boolean }) => void;
    initialName?: string;
};

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

export const CustomerQuickCreateDialog: React.FC<Props> = ({ open, onClose, onSaved, initialName }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [defaults, setDefaults] = useState<CustomerEdit | null>(null);

    const [name, setName] = useState(initialName ?? "");
    const [businessId, setBusinessId] = useState("");
    const [vatCode, setVatCode] = useState("");
    const [erpReference, setErpReference] = useState("");
    const [invoiceStreet, setInvoiceStreet] = useState("");
    const [invoicePostalCode, setInvoicePostalCode] = useState("");
    const [invoiceCity, setInvoiceCity] = useState("");
    const [invoiceCountryCode, setInvoiceCountryCode] = useState("");
    const [invoiceEmail, setInvoiceEmail] = useState("");
    const [orderPhone, setOrderPhone] = useState("");
    const [enabled, setEnabled] = useState(true);

    const [eInvoiceAddress, setEInvoiceAddress] = useState("");
    const [eInvoiceOperator, setEInvoiceOperator] = useState("");

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        getCustomerEdit(EMPTY_GUID)
            .then((res) => {
                setDefaults(res);
                setName((prev) => prev || "");
                setInvoiceCountryCode((res)?.customer?.invoiceCountryCode ?? "");
            })
            .catch(showApiError)
            .finally(() => setLoading(false));
    }, [open]);

    const canSave = useMemo(() => !!name && !loading && !!defaults?.customer, [name, loading, defaults]);

    const handleSave = async() => {
        if (!canSave) return;

        try {
            const fd = new FormData();

            // Required boilerplate for CustomerSaveData
            fd.append("id", EMPTY_GUID);
            fd.append("rowId", "");

            // Required but hidden defaults provided by GET api/customer/edit
            fd.append("customerTypeId", defaults.customer.customerTypeId);
            fd.append("workOrderAcknowledgementTypeId", defaults.customer.workOrderAcknowledgementTypeId);
            fd.append("invoicePeriodId", defaults.customer.invoicePeriodId);

            // Visible minimal fields
            fd.append("name", name?.trim() ?? "");
            fd.append("erp", erpReference?.trim() ?? "");
            fd.append("invoiceStreet", invoiceStreet ?? "");
            fd.append("invoicePostalCode", invoicePostalCode ?? "");
            fd.append("invoiceCity", invoiceCity ?? "");
            fd.append("invoiceCountryCode", invoiceCountryCode ?? "");
            fd.append("invoiceEmail", invoiceEmail ?? "");
            fd.append("eInvoiceAddress", eInvoiceAddress ?? "");
            fd.append("eInvoiceOperator", eInvoiceOperator ?? "");
            fd.append("orderContactNumber", orderPhone ?? "");
            fd.append("businessIdentityCode", businessId ?? "");
            fd.append("vatCode", vatCode ?? "");
            fd.append("activeState", enabled ? "1" : "0");

            // Collections (empty by default)
            fd.append("sites", JSON.stringify([]));
            fd.append("removedSiteIds", JSON.stringify([]));
            fd.append("addedCustomerContactContactIds", JSON.stringify([]));
            fd.append("removedCustomerContactIds", JSON.stringify([]));
            fd.append("routePoints", JSON.stringify([]));
            fd.append("removedRoutePointIds", JSON.stringify([]));
            fd.append("routePointChecks", JSON.stringify([]));
            fd.append("removedRoutePointCheckIds", JSON.stringify([]));
            fd.append("customerPrices", JSON.stringify([]));
            fd.append("removedCustomerPriceIds", JSON.stringify([]));
            fd.append("costCenterIds", JSON.stringify([]));

            const res = await saveCustomerEdit(fd);
            onSaved({ id: res.id, name, active: enabled });
            onClose();
        } catch (err) {
            showApiError(err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>{t("transport.order.customerCreate.title")}</DialogTitle>
            <DialogContent
                dividers
                sx={{ px: 3, py: 2 }}
                // Prevent parent DataGrid keyboard handlers from capturing Tab so focus moves to the next field in the dialog
                onKeyDown={(e) => {
                    if (e.key === "Tab") {
                        e.stopPropagation();
                    }
                }}
            >
                <Box display="flex" flexDirection="column" gap={3} mt={1}>
                    {/* Basic Information Section */}
                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("transport.order.customerCreate.basicInformation")}
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                required
                                label={t("common.name")}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                autoFocus
                                fullWidth
                                sx={{ maxWidth: 600 }}
                            />
                            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }} gap={2}>
                                <TextField
                                    label={t("customer.businessIdentityCode")}
                                    value={businessId}
                                    onChange={(e) => setBusinessId(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label={t("customer.vatCode")}
                                    value={vatCode}
                                    onChange={(e) => setVatCode(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label={t("customer.erpReference")}
                                    value={erpReference}
                                    onChange={(e) => setErpReference(e.target.value)}
                                    fullWidth
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Divider />

                    {/* Contact Information Section */}
                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("transport.order.customerCreate.contactInformation")}
                        </Typography>
                        <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                            <TextField
                                label={t("common.email")}
                                value={invoiceEmail}
                                onChange={(e) => setInvoiceEmail(e.target.value)}
                                type="email"
                                fullWidth
                            />
                            <TextField
                                label={t("customer.orderPhone")}
                                value={orderPhone}
                                onChange={(e) => setOrderPhone(e.target.value)}
                                type="tel"
                                fullWidth
                            />
                        </Box>
                    </Box>

                    <Divider />

                    {/* Invoice Address Section */}
                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("customer.invoiceAddress")}
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={2}>
                            <TextField
                                label={t("common.streetAddress")}
                                value={invoiceStreet}
                                onChange={(e) => setInvoiceStreet(e.target.value)}
                                fullWidth
                            />
                            <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 2fr", md: "1fr 2fr 1fr" }} gap={2}>
                                <TextField
                                    label={t("common.postalCode")}
                                    value={invoicePostalCode}
                                    onChange={(e) => setInvoicePostalCode(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label={t("common.cityAddress")}
                                    value={invoiceCity}
                                    onChange={(e) => setInvoiceCity(e.target.value)}
                                    fullWidth
                                />
                                <TextField
                                    label={t("common.countryCode")}
                                    value={invoiceCountryCode}
                                    onChange={(e) => setInvoiceCountryCode(e.target.value)}
                                    placeholder="FI"
                                    fullWidth
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Divider />

                    {/* Electronic Invoicing Section */}
                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("transport.order.customerCreate.electronicInvoicing")}
                        </Typography>
                        <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
                            <TextField
                                label={t("customer.eInvoiceAddress")}
                                value={eInvoiceAddress}
                                onChange={(e) => setEInvoiceAddress(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label={t("customer.eInvoiceOperator")}
                                value={eInvoiceOperator}
                                onChange={(e) => setEInvoiceOperator(e.target.value)}
                                fullWidth
                            />
                        </Box>
                    </Box>

                    <Divider />

                    {/* Settings Section */}
                    <Box>
                        <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                            {t("transport.order.customerCreate.settings")}
                        </Typography>
                        <FormControlLabel
                            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
                            label={t("common.inUse")}
                        />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    {t("common.cancel")}
                </Button>
                <Button variant="contained" disabled={!canSave} onClick={() => void handleSave()}>
                    {t("common.save")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};